import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui';
import { requestNotificationPermission, saveNotificationSettings, scheduleDailyNotifications } from '@/utils/dailyNotifications';

interface NotificationPromptProps {
  onDismiss: () => void;
}

const NotificationPrompt: React.FC<NotificationPromptProps> = ({ onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    // Check if user has already been prompted
    const hasBeenPrompted = localStorage.getItem('riddl-notification-prompted');
    const hasPermission = Notification.permission === 'granted';
    
    // Check if app is running as PWA
    const pwaStatus = window.matchMedia('(display-mode: standalone)').matches || 
                     ('standalone' in window.navigator && (window.navigator as Navigator & { standalone?: boolean }).standalone === true) ||
                     document.referrer.includes('android-app://');
    setIsPWA(pwaStatus);
    
    if (!hasBeenPrompted && hasPermission === false) {
      // Show prompt after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleEnableNotifications = async () => {
    setIsRequesting(true);
    
    try {
      const permission = await requestNotificationPermission();
      
      if (permission === 'granted') {
        // Save notification settings
        saveNotificationSettings({
          enabled: true,
          morningTime: '09:00',
          afternoonTime: '14:00',
          eveningTime: '18:00',
        });
        
        // Mark as prompted
        localStorage.setItem('riddl-notification-prompted', 'true');
        
        // Schedule notifications
        await scheduleDailyNotifications();
        
        setIsVisible(false);
        onDismiss();
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    // Mark as prompted so we don't show again
    localStorage.setItem('riddl-notification-prompted', 'true');
    setIsVisible(false);
    onDismiss();
  };


  if (!isVisible) {
    return null;
  }

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6'>
        <div className='text-center'>
          <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-teal-100 dark:bg-teal-900 mb-4'>
            <span className='text-2xl'>🔔</span>
          </div>
          
          <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
            Never Miss a Riddle!
          </h3>
          
          <p className='text-sm text-gray-600 dark:text-gray-400 mb-4'>
            Get daily reminders when new riddles are available. We'll notify you at 9am, 2pm, and 6pm local time (only if you haven't solved today's riddle yet).
          </p>
          
          {!isPWA && (
            <div className='p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md mb-4'>
              <p className='text-xs text-blue-800 dark:text-blue-200'>
                💡 <strong>Browser Mode:</strong> Notifications work when this tab is open. 
                Install as a PWA for background notifications when the app is closed!
              </p>
            </div>
          )}
          
          <div className='flex space-x-3'>
            <Button
              variant='secondary'
              onClick={handleDismiss}
              className='flex-1'
            >
              Not Now
            </Button>
            
            <Button
              variant='primary'
              onClick={handleEnableNotifications}
              disabled={isRequesting}
              className='flex-1'
            >
              {isRequesting ? 'Enabling...' : 'Enable Notifications'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;
